import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useAnimatedReaction, useSharedValue } from 'react-native-reanimated';
import { useScreenSize } from '@banhala/ui-kit';
import { scheduleOnRN } from 'react-native-worklets';
import { ScrollContext } from '../context/ScrollContext';
import { isNotNil } from 'shared/src/utils';
import { type View } from 'react-native';
import { useInstanceId } from 'shared/src/hooks';

const sectionVisibilityAtom = atom<Record<string, Record<string, boolean>>>(
  {},
);

export const useIsSectionVisible = (key: string) => {
  const instanceId = useInstanceId();
  const derivedAtom = useMemo(
    () =>
      atom(get => isNotNil(get(sectionVisibilityAtom)[instanceId]?.[key])),
    [instanceId, key],
  );
  return useAtomValue(derivedAtom);
};

export const useMarkSectionVisible = () => {
  const instanceId = useInstanceId();
  const set = useSetAtom(sectionVisibilityAtom);
  return useCallback(
    (key: string) => {
      set(prev => {
        if (prev[instanceId]?.[key]) return prev;
        return {
          ...prev,
          [instanceId]: { ...prev[instanceId], [key]: true },
        };
      });
    },
    [instanceId, set],
  );
};

/**
 * 컴포넌트가 뷰포트에 진입했는지 감지하는 훅.
 * 중첩된 서브 섹션 컴포넌트에서 사용.
 *
 * 반환된 ref를 View에 연결하면,
 * 스크롤 시 해당 요소가 화면에 보이는 순간 visibility atom을 업데이트.
 */
export const useElementViewportTracking = (
  visibilityKey: string,
  offset = 0,
) => {
  const { scrollY } = useContext(ScrollContext);
  const { height: screenHeight } = useScreenSize();
  const isMarked = useSharedValue(false);
  const viewRef = useRef<View>(null);
  const markVisible = useMarkSectionVisible();

  const checkVisibility = useCallback(() => {
    if (isMarked.value) return;
    viewRef.current?.measureInWindow((_x, y, _w, h) => {
      if (h > 0 && y < screenHeight - offset) {
        isMarked.value = true;

        markVisible(visibilityKey);
      }
    });
  }, [isMarked, screenHeight, offset, markVisible, visibilityKey]);

  useAnimatedReaction(
    () => scrollY?.value ?? 0,
    () => {
      if (isMarked.value) return;
      scheduleOnRN(checkVisibility);
    },
    [checkVisibility],
  );

  return viewRef;
};
